import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Toolbar,
  Box,
  LinearProgress,
  TextField,
  InputAdornment,
  Chip,
  Alert,
  Divider,
  Card,
  CardContent,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Description as DescriptionIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useViewerData } from './viewerHttp';

// Theme colors
const primaryColor = '#5757b0';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '95vw',
    maxHeight: '95vh',
    width: '85vw',
    height: '85vh',
    backgroundColor: '#f5f5f5',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 8px 32px rgba(87, 87, 176, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  '& .MuiDialog-paper.MuiDialog-paperFullScreen': {
    width: '100vw',
    height: '100vh',
    maxWidth: '100vw',
    maxHeight: '100vh',
    borderRadius: 0,
    margin: 0,
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiDialog-paper': {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      borderRadius: 0,
      margin: 0,
    },
  },
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(87, 87, 176, 0.15)',
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${primaryColor} 0%, #6b6bce 50%, #8080d7 100%)`,
  color: 'white',
  padding: '12px 20px',
  paddingTop: 'calc(12px + env(safe-area-inset-top))',
  paddingLeft: 'calc(20px + env(safe-area-inset-left))',
  paddingRight: 'calc(20px + env(safe-area-inset-right))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontWeight: 700,
  fontSize: '1.1rem',
  position: 'relative',
  boxShadow: '0 2px 8px rgba(87, 87, 176, 0.25)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
  '& .MuiTypography-root': {
    flex: 1,
    margin: 0,
    fontWeight: 600,
    fontSize: '1.1rem',
    letterSpacing: '0.025em',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
  },
  '& > .MuiBox-root': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
  },
  '& > .MuiBox-root > .MuiTypography-root': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& > .MuiIconButton-root': {
    flexShrink: 0,
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px 12px',
    paddingTop: 'calc(10px + env(safe-area-inset-top))',
    paddingLeft: 'calc(12px + env(safe-area-inset-left))',
    paddingRight: 'calc(12px + env(safe-area-inset-right))',
    fontSize: '1rem',
    flexWrap: 'nowrap',
    gap: '8px',
    '& .MuiTypography-root': {
      fontSize: '1rem',
      minWidth: 0,
      wordBreak: 'break-word',
    },
  },
}));

const ToolbarContainer = styled(Toolbar)(({ theme }) => ({
  backgroundColor: 'white',
  borderBottom: '1px solid rgba(87, 87, 176, 0.1)',
  minHeight: '48px !important',
  padding: '0 20px',
  justifyContent: 'space-between',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: '0 12px',
    minHeight: '44px !important',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'flex-start',
  },
}));

const WordContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  backgroundColor: '#f8f9fa',
  overflow: 'hidden',
}));

const DocumentContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  margin: '16px',
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(87, 87, 176, 0.1)',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    margin: '8px',
  },
}));

const DocumentContent = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: '32px',
  overflow: 'auto',
  fontSize: '14px',
  [theme.breakpoints.down('sm')]: {
    padding: '16px',
  },
  lineHeight: '1.6',
  fontFamily: '"Segoe UI", "Roboto", "Arial", sans-serif',
  color: '#2d3748',
  backgroundColor: '#ffffff',
  
  // Document-like styling
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    fontWeight: 600,
    marginTop: '24px',
    marginBottom: '16px',
    color: '#1a202c',
  },
  '& h1': {
    fontSize: '2em',
    borderBottom: '2px solid #e2e8f0',
    paddingBottom: '8px',
  },
  '& h2': {
    fontSize: '1.5em',
  },
  '& h3': {
    fontSize: '1.25em',
  },
  '& p': {
    marginBottom: '16px',
  },
  '& ul, & ol': {
    marginBottom: '16px',
    paddingLeft: '32px',
  },
  '& li': {
    marginBottom: '8px',
  },
  '& blockquote': {
    borderLeft: '4px solid #e2e8f0',
    paddingLeft: '16px',
    marginLeft: '0',
    marginBottom: '16px',
    fontStyle: 'italic',
    color: '#4a5568',
  },
  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '16px',
  },
  '& th, & td': {
    border: '1px solid #e2e8f0',
    padding: '8px 12px',
    textAlign: 'left',
  },
  '& th': {
    backgroundColor: '#f7fafc',
    fontWeight: 600,
  },
  '& pre': {
    backgroundColor: '#f7fafc',
    padding: '16px',
    borderRadius: '4px',
    overflow: 'auto',
    fontSize: '13px',
    fontFamily: 'monospace',
    marginBottom: '16px',
  },
  '& code': {
    backgroundColor: '#f7fafc',
    padding: '2px 4px',
    borderRadius: '3px',
    fontSize: '13px',
    fontFamily: 'monospace',
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    marginBottom: '16px',
  },
  '& hr': {
    border: 'none',
    borderTop: '1px solid #e2e8f0',
    margin: '32px 0',
  },
  
  // Custom scrollbar
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#f1f1f1',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#c1c1c1',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: '#a8a8a8',
    },
  },
}));

const InfoPanel = styled(Card)(({ theme }) => ({
  width: '280px',
  margin: '16px 0 16px 16px',
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(87, 87, 176, 0.1)',
  display: 'flex',
  flexDirection: 'column',
  maxHeight: 'calc(100vh - 200px)',
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  padding: '16px',
  borderBottom: '1px solid rgba(87, 87, 176, 0.1)',
}));

const StatsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  '& .MuiChip-root': {
    justifyContent: 'flex-start',
  },
}));

const WordViewer = ({ open, onClose, filename = '', eventId = null, filePath = '' }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    data: wordData,
    loading,
    error,
  } = useViewerData({
    open,
    eventId,
    filePath,
    missingMessage: t('viewer.word_missing_source', 'File id or path is required'),
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSearchTerm('');
    }
  }, [open, eventId, filename, filePath]);

  const downloadWord = () => {
    if (wordData) {
      // Create a formatted text export
      const content = [
        `Document: ${filename}`,
        `Title: ${wordData.title || 'Untitled'}`,
        `Author: ${wordData.author || 'Unknown'}`,
        `Created: ${wordData.created_date || 'Unknown'}`,
        `Modified: ${wordData.modified_date || 'Unknown'}`,
        '',
        '--- Content ---',
        '',
        wordData.content || 'No content available'
      ].join('\n');
      
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename || 'document'}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (containerRef.current && containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (error) {
      return dateString;
    }
  };

  // Get document statistics
  const getStats = () => {
    if (!wordData || !wordData.content) return { words: 0, characters: 0, paragraphs: 0, pages: 0 };
    
    const content = wordData.content;
    const words = content.trim().split(/\s+/).length;
    const characters = content.length;
    const paragraphs = content.split(/\n\s*\n/).length;
    const pages = Math.ceil(words / 250); // Approximate 250 words per page
    
    return { words, characters, paragraphs, pages };
  };

  const stats = getStats();
  const renderedContent = useMemo(() => {
    const content = wordData?.content || 'No content available';
    const escaped = content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

    if (!searchTerm) {
      return `<pre style="white-space: pre-wrap; margin: 0; font-family: inherit;">${escaped}</pre>`;
    }

    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const highlighted = escaped.replace(regex, '<mark style="background-color: #ffeb3b; padding: 0 2px; border-radius: 2px;">$1</mark>');
    return `<pre style="white-space: pre-wrap; margin: 0; font-family: inherit;">${highlighted}</pre>`;
  }, [wordData, searchTerm]);

  return (
    <StyledDialog 
      open={open} 
      onClose={onClose}
      maxWidth={false}
      fullScreen={isFullscreen || isSmallScreen}
    >
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <DescriptionIcon sx={{ 
            mr: 1.5, 
            fontSize: '1.4rem',
            filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2))'
          }} />
          <Typography variant="h6" component="div" sx={{ margin: 0 }}>
            {filename ? `${t('viewer.word_viewer_title')} - ${filename}` : t('viewer.word_viewer_title')}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ 
            color: 'white',
            marginLeft: 'auto',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(4px)',
            borderRadius: '8px',
            width: '36px',
            height: '36px',
            transition: 'all 0.3s ease',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              transform: 'scale(1.05)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              borderColor: 'rgba(255, 255, 255, 0.4)',
            },
          }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <ToolbarContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            onClick={downloadWord} 
            disabled={!wordData}
            size="small"
            title={t('viewer.download_word')}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton 
            onClick={toggleFullscreen}
            size="small"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Box>
        
        {wordData && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={`${stats.words} words`}
              size="small"
              variant="outlined"
            />
            <Chip 
              label={`${stats.pages} pages`}
              size="small"
              variant="outlined"
            />
          </Box>
        )}
      </ToolbarContainer>

      <DialogContent sx={{ "--dialog-content-top-padding": "0px", padding: 0, height: '100%', overflow: 'hidden' }}>
        <WordContainer ref={containerRef}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              width: '100%',
              gap: 2
            }}>
              <LinearProgress sx={{ width: 300 }} />
              <Typography color="text.secondary">
                {t('viewer.loading_word')}
              </Typography>
            </Box>
          ) : error ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              width: '100%',
              padding: 4
            }}>
              <Typography color="error" variant="h6" gutterBottom>
                {t('viewer.error_loading_word')}
              </Typography>
              <Typography color="text.secondary" sx={{ textAlign: 'center' }}>
                {error}
              </Typography>
            </Box>
          ) : wordData ? (
            <>
              {/* Info Panel */}
              <InfoPanel>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoIcon color="primary" />
                    Document Info
                  </Typography>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Title
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {wordData.title || 'Untitled'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Author
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {wordData.author || 'Unknown'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Created
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatDate(wordData.created_date)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Modified
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatDate(wordData.modified_date)}
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <StatsContainer>
                    <Chip 
                      label={`${stats.words} words`}
                      variant="outlined"
                      size="small"
                    />
                    <Chip 
                      label={`${stats.characters} characters`}
                      variant="outlined"
                      size="small"
                    />
                    <Chip 
                      label={`${stats.paragraphs} paragraphs`}
                      variant="outlined"
                      size="small"
                    />
                    <Chip 
                      label={`≈${stats.pages} pages`}
                      variant="outlined"
                      size="small"
                    />
                  </StatsContainer>
                </CardContent>
              </InfoPanel>

              {/* Document Content */}
              <DocumentContainer>
                <SearchContainer>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t('viewer.search_placeholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={clearSearch}>
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </SearchContainer>
                <Alert
                  severity="warning"
                  variant="outlined"
                  sx={{ mx: 2, mt: 0, mb: 2 }}
                >
                  {t('viewer.word_preview_warning', 'This is a text extraction preview. The formatting may not match the original Word document.')}
                </Alert>
                
                <DocumentContent
                  dangerouslySetInnerHTML={{ __html: renderedContent }}
                />
              </DocumentContainer>
            </>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              width: '100%',
              padding: 4
            }}>
              <Typography color="text.secondary">
                {t('viewer.no_word_content')}
              </Typography>
            </Box>
          )}
        </WordContainer>
      </DialogContent>
    </StyledDialog>
  );
};

export default WordViewer;
